import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/feature/PageHeader";
import { dailyPosts } from "@/mocks/dailyPosts";

const CATEGORIES = ["全部", ...Array.from(new Set(dailyPosts.map((p) => p.category)))];

export default function Daily() {
  const [active, setActive] = useState("全部");
  const list = useMemo(
    () => (active === "全部" ? dailyPosts : dailyPosts.filter((p) => p.category === active)),
    [active],
  );
  const [featured, ...rest] = list;

  return (
    <>
      <Navbar />
      <main>
        <PageHeader
          eyebrow="SECT DAILY"
          title="师门日常"
          subtitle="抢灵果、炸丹炉、偷跑下山……云隐峰弟子们的日常片段全收录，吵吵闹闹里全是剪不断的同门情谊。"
          crumbs={[{ label: "首页", path: "/" }, { label: "师门日常" }]}
          image="https://readdy.ai/api/search-image?query=Cozy%20lively%20scene%20inside%20a%20Chinese%20cultivation%20sect%20courtyard%20with%20disciples%20chatting%2C%20lanterns%2C%20tea%20and%20spirit%20fruits%20on%20tables%2C%20warm%20ink%20wash%20style%2C%20golden%20hour%20light%2C%20cheerful%20atmosphere%2C%20refined%20detail&width=1920&height=760&seq=page-daily-01&orientation=landscape"
        />

        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="师门日常分类筛选">
            {CATEGORIES.map((c) => {
              const activeTab = active === c;
              return (
                <button
                  key={c}
                  type="button"
                  role="tab"
                  aria-selected={activeTab}
                  onClick={() => setActive(c)}
                  className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm transition-colors ${
                    activeTab
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>

          {featured && (
            <article className="mt-8 grid grid-cols-1 gap-0 overflow-hidden rounded-lg border border-background-200 bg-background-100 lg:grid-cols-2">
              <div className="relative h-64 w-full lg:h-auto">
                <img src={featured.cover} alt={`${featured.title} 师门日常插画`} className="h-full w-full object-cover" />
              </div>
              <div className="flex flex-col justify-center p-8 md:p-10">
                <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                  <span className="rounded-full bg-primary-500/10 px-3 py-1 text-primary-700">编辑精选</span>
                  <span>{featured.category}</span>
                  <span>更新于 {featured.date}</span>
                </div>
                <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  {featured.title}
                </h2>
                <p className="mt-4 text-sm leading-7 text-foreground-600">{featured.excerpt}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {featured.tags.map((t) => (
                    <span key={t} className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-600">
                      #{t}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          )}

          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((post) => (
              <article key={post.id} className="group overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-all hover:-translate-y-1 hover:border-primary-200">
                <div className="relative h-44 w-full overflow-hidden">
                  <img
                    src={post.cover}
                    alt={`${post.title} 师门日常插画`}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background-950/70 px-3 py-1 text-xs text-accent-200 backdrop-blur">
                    {post.category}
                  </span>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-time-line" />
                    <span>更新于 {post.date}</span>
                  </div>
                  <h3 className="mt-2 font-heading text-lg leading-6 text-foreground-950 group-hover:text-primary-700">
                    {post.title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600 line-clamp-2">{post.excerpt}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm text-foreground-600">想看更多角色关系与互动细节？</p>
            <Link
              to="/relations"
              className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              前往人物关系图 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Blog",
              name: "师门日常",
              description: "云隐峰弟子们的日常片段与名场面收录。",
              inLanguage: "zh-CN",
            }),
          }}
        />
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}